import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import { AlertColor as AlertSeverity } from '@mui/lab';
import { IconButton, Tooltip } from '@mui/material';
import { orange } from '@mui/material/colors';
import makeStyles from '@mui/styles/makeStyles';
import {
  GridRenderCellParams,
  GridRowData,
  GridSortModel,
  GridValueGetterParams,
} from '@mui/x-data-grid';
import { AxiosError, AxiosResponse, CancelTokenSource } from 'axios';
import { History } from 'history';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation } from 'react-query';
import { generatePath, Link, NavLink, Route, Switch } from 'react-router-dom';
import {
  useRecoilStateLoadable,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';

import { useBusinessPermissions } from '../../../../common/helperFunctions/useBusinessPermissions';
import { Logger } from '../../../../common/Logger';
import { trackAddQueue } from '../../../../common/userTracking';
import { Cadence } from '../../../../lib/kixie-entities/tables/Cadence';
import { IUserAtom, user } from '../../../../recoil/atoms/user';
import {
  makePHPRequest,
  methodNameEnum,
  refetchToggle,
} from '../../../../recoil/selectors/makePHPRequest';
import { getApiMutation } from '../../../../services/rest/getApiMutation';
import { endpointEnum } from '../../../../services/rest/makeAPIRequest';
import { useSnackbarWithStyles } from '../../../components/notifications/SnackbarProviderWithStyles';
import { ErrorOnValidate } from '../../../pages/ManageCadences2';

import { QueueEditDialog } from './queue-edit-dialog/QueueEditDialog';
import { QueueMembersDialog } from './queue-edit-dialog/QueueMembersDialog';
import { QueuesDeleteDialog } from './queues-delete-dialog/QueuesDeleteDialog';
import { QueuesLoadingDialog } from './queues-loading-dialog/QueuesLoadingDialog';
import { ActionColumn } from './queues-table/ActionColumns';
import dataTableStyles from './queues-table/data-table.module.css';
import {
  DataTable,
  IColDef,
  IToolbarComponent,
} from './queues-table/DataTable';
import {
  EnhancedTableToolbar,
  ToolbarAddDeleteButton,
} from './queues-table/EnhancedTableToolbar';
// import { DashboardTourExtraSpotlights } from '../../../pages/dashboardTour/DashboardTourExtraSpotlights';

const useStyles = makeStyles({
  peopleIconButton: {
    color: orange[500],
  },
});

interface IQueuesPage {
  /** React Router history used to redirect to modal, etc. */
  history: History;
}

/**
 * Show DataGrid table with controls to show modals (with dedicated routes)
 */
export function QueuesPage({ history }: IQueuesPage) {
  const classes = useStyles();
  const [selected, setSelected] = useState<GridRowData[]>([]);
  const [showMembersTab, setShowMembersTab] = useState<boolean>(false);
  const [deleteItemId, setDeleteItemId] = useState<
    string | number | undefined
  >();
  const [deleteCancelTokenSource, setDeleteCancelTokenSource] = useState<
    CancelTokenSource | undefined
  >();

  // get atoms and selectors
  const [userAtomLoadable, setUserAtom] = useRecoilStateLoadable<
    IUserAtom | Record<string, unknown>
  >(user);
  const userAtomContents = userAtomLoadable.contents as IUserAtom;
  const businessQueuesLoadable = useRecoilValueLoadable(
    makePHPRequest({ methodName: methodNameEnum.GET_BUSINESS_QUEUES })
  );
  const setRefetchToggleForBusinessQueues = useSetRecoilState(
    refetchToggle(methodNameEnum.GET_BUSINESS_QUEUES)
  );
  const mutation = useMutation(
    getApiMutation(userAtomContents, setUserAtom, setDeleteCancelTokenSource)
  );
  // notification using snackbar
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();

  /**
   * Transform by adding id
   * NOTE: must use useMemo so rows do not get updated on re-render
   */
  const rowsCached = useRef<any[]>([]);
  rowsCached.current = React.useMemo(() => {
    if (businessQueuesLoadable.state === 'hasValue') {
      const result = businessQueuesLoadable.contents?.data?.result;
      if (Array.isArray(result)) {
        return result.map((row) => ({
          ...row,
          id: +row.queueid, // allow number sorting
        }));
      }
      return [];
    }

    return rowsCached.current;
  }, [
    businessQueuesLoadable.state,
    businessQueuesLoadable.contents?.data?.result,
  ]);

  /**
   * Callback used by column button to delete queue
   * @param id Queue to be deleted
   */
  const handleDeleteQueue = async (id: string | number) => {
    // const { response, exception, status } = await phpRequest({
    //   methodName: methodNameEnum.DELETE_QUEUE,
    //   payload: {
    //     queueid: id,
    //   },
    // });
    //     console.log('i got hit not fonna make it in delete queueueue-------')
    let message: string | React.ReactNode = 'Update successful'; // ! to ignore usage before assignment error
    let severity: AlertSeverity = 'success'; // ! to ignore usage before assignment error

    const data = {
      call: methodNameEnum.DELETE_QUEUE,
      apikey: userAtomContents.apikey,
      businessid: userAtomContents.businessid,
      queueid: id,
    };
    let response!: AxiosResponse<any>;
    let exception!: AxiosError | Error;
    try {
      response = await mutation.mutateAsync({
        endpoint: endpointEnum.DELETE_QUEUE,
        payload: data,
      });

      // clear selections list
      setDeleteItemId(undefined);
      // refetch business queues
      setRefetchToggleForBusinessQueues({});

      if (response.data.success === false) {
        if (response.data.errorOnValidate) {
          const errorOnValidate = response.data
            .errorOnValidate as ErrorOnValidate;
          throw Object.assign(
            new ErrorOnValidate(errorOnValidate.message),
            errorOnValidate
          );
        }

        throw new Error(response.data.message ?? 'Update failed');
      }
    } catch (ex) {
      exception = ex as AxiosError | Error;

      message = 'Failed to remove queue';
      severity = 'error';

      if (
        ex instanceof ErrorOnValidate &&
        ex.errorType === 'FK_CADENCE__QUEUES' &&
        ex.entities
      ) {
        const cadences: Cadence[] = ex.entities;
        message = (
          <div>
            Cannot remove queue since it's used in the following cadences:
            <ul>
              {cadences.map((cadence) => (
                <li key={cadence.cadenceId}>
                  <a
                    href={`/manage/cadences/${cadence.cadenceId}/edit`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: 'white',
                      textDecoration: 'underline',
                    }}
                  >
                    {cadence.cadenceName}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        );
        severity = 'warning';
      } else if ((ex as AxiosError).isAxiosError) {
        const axiosError = ex as AxiosError;
        message = axiosError.message;
      }
    }

    // log success
    Logger.trace(
      {
        message,
        response,
        exception,
      },
      'QueuesPage:handleDeleteQueue'
    );

    enqueueSnackbarWithStyles({
      message,
      alertProps: {
        severity,
      },
    });
  };

  const handleFlush = async (id: string | number) => {
    let message: string | React.ReactNode = 'Update successful'; // ! to ignore usage before assignment error
    let severity: AlertSeverity = 'success'; // ! to ignore usage before assignment error

    const data = {
      call: methodNameEnum.FLUSH_USER_QUEUE,
      apikey: userAtomContents.apikey,
      businessid: userAtomContents.businessid,
      id,
    };

    let response!: AxiosResponse<any>;
    let exception!: AxiosError | Error;
    try {
      response = await mutation.mutateAsync({
        endpoint: endpointEnum.FLUSH_USER_QUEUE,
        payload: data,
      });

      // clear selections list
      setDeleteItemId(undefined);
      // refetch business queues
      setRefetchToggleForBusinessQueues({});
      if (response.data.success === false) {
        if (response.data.errorOnValidate) {
          const errorOnValidate = response.data
            .errorOnValidate as ErrorOnValidate;
          throw Object.assign(
            new ErrorOnValidate(errorOnValidate.message),
            errorOnValidate
          );
        }

        throw new Error(response.data.message ?? 'Update failed');
      }
    } catch (ex) {
      exception = ex as AxiosError | Error;

      message = 'Failed to flush queue';
      severity = 'error';

      if ((ex as AxiosError).isAxiosError) {
        const axiosError = ex as AxiosError;
        message = axiosError.message;
      }
    }

    // log success
    Logger.trace(
      {
        message,
        response,
        exception,
      },
      'QueuesPage:handleDeleteQueue'
    );

    enqueueSnackbarWithStyles({
      message,
      alertProps: {
        severity,
      },
    });
  };

  function nameFormatter(params) {
    return (
      <Tooltip title="Edit">
        <Link to={`/manage/queues/${params.row.id}/edit`}>{params.value}</Link>
      </Tooltip>
    );
  }
  function teamFormatter(params: GridRenderCellParams) {
    const splitNames = (params.value as string)?.split(',');
    if (splitNames?.length > 1) {
      return (
        <Tooltip title="Edit Members">
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {splitNames?.slice(0, 2)?.map((name, i) => (
              <Link
                key={`${name}-${i}`}
                to={`/manage/queues/${params.row.id}/edit`}
                onClick={() => {
                  setShowMembersTab(true);
                }}
              >
                {name}
              </Link>
            ))}
            <>
              {splitNames?.length > 2 && (
                <Link
                  to={`/manage/queues/${params.row.id}/edit`}
                  onClick={() => setShowMembersTab(true)}
                >
                  + {splitNames?.length - 2} more ...
                </Link>
              )}
            </>
          </div>
        </Tooltip>
      );
    } else {
      return (
        <Tooltip title="Edit Members">
          <NavLink
            to={`/manage/queues/${params.row.id}/edit`}
            onClick={() => setShowMembersTab(true)}
          >
            {params.value}
          </NavLink>
        </Tooltip>
      );
    }
  }
  const readOnly = useBusinessPermissions().accountReadOnly;

  /**
   * Create column for DataGrid renderer
   * use useMemo to prevent unneeded rerenderings
   */
  const columns = useMemo(
    () =>
      [
        {
          field: 'id',
          headerName: 'ID',
          width: 70,
        },
        {
          field: 'queuename',
          headerName: 'Name',
          // width: 130,
          headerClassName: dataTableStyles.fullWidth,
          cellClassName: dataTableStyles.fullWidth,
          renderCell: nameFormatter,
        },
        {
          field: 'teamMemberCount',
          headerName: 'Members',
          // width: 130,
          headerClassName: dataTableStyles.fullWidth,
          cellClassName: dataTableStyles.fullWidth,
        },
        {
          field: 'teamNames',
          headerName: 'Teams',
          // width: 130,
          headerClassName: dataTableStyles.fullWidth,
          cellClassName: dataTableStyles.fullWidth,
          renderCell: teamFormatter,
        },
        {
          field: 'email',
          headerName: 'Email',
          // width: 130,
          headerClassName: dataTableStyles.fullWidth,
          cellClassName: dataTableStyles.fullWidth,
        },
        {
          field: 'hasGreeting',
          headerName: 'Greeting',
          type: 'number',
          headerAlign: 'left',
        },
        {
          field: 'hasRingMusic',
          headerName: 'Ring Audio',
          type: 'number',
        },
        {
          field: 'action',
          headerName: 'Action',
          headerAlign:
            userAtomContents?.user?.role === 'superadmin' ? 'center' : 'right',
          width: 216,
          hide: readOnly,
          disableSearch: true, // do not search field content
          disableRowSelection: true, // to have onlcick to work row selection should be disabled
          enableSorting: false,
          renderCell: ActionColumn({
            deleteItemId,
            setDeleteItemId,
            isLoading: mutation.isLoading,
            handleDeletion: handleDeleteQueue,
            editLink: '/manage/queues/:id/edit',
            deleteCancelTokenSource,
            renderAction: (params: GridValueGetterParams) => (
              <Tooltip title="Members">
                <NavLink
                  to={
                    // ENG-7393 fixed memory leak due to Uncontrolled re-renders: Frequent calls to generatePath and string concatenation.
                    generatePath('/manage/queues/:id/members', {
                      id: params.row.id,
                    }) +
                    `?queuename=${encodeURIComponent(
                      params.row.queuename || ''
                    )}`
                  }
                >
                  <IconButton name="members" size="large">
                    <PeopleOutlineIcon className={classes.peopleIconButton} />
                  </IconButton>
                </NavLink>
              </Tooltip>
            ),
            handleFlush:
              userAtomContents?.user?.role === 'superadmin'
                ? handleFlush
                : undefined,
          }),
        },
      ] /* satisfies */ as IColDef[],
    [deleteItemId, mutation.isLoading, userAtomContents?.user?.role]
  );

  /**
   * Create cutom toolbar component for table
   * Use useCallback to stop rerendering
   */
  const ToolbarComponent = useCallback(
    (props: IToolbarComponent) => (
      <EnhancedTableToolbar
        {...props}
        dataType="Queues"
        numSelected={selected.length}
        rightComponent={
          <ToolbarAddDeleteButton
            className="manageQueuesAdd"
            numSelected={selected.length}
            onClickForAdd={() => {
              trackAddQueue();
              history.push('/manage/queues/new');
            }}
            onClickForDelete={() => {
              history.push('/manage/queues/deleteQueues');
            }}
            isLoading={props.isLoading}
          />
        }
      />
    ),
    [selected.length]
  );

  const [updatedRowId, setUpdatedRowId] = useState<string | undefined>();
  const [sortModel, setGridSortModel] = React.useState<GridSortModel>([
    { field: 'id', sort: 'asc' },
  ]);
  return (
    <>
      {/* <DashboardTourExtraSpotlights /> */}
      <QueuesLoadingDialog />
      <Switch>
        <Route path="/manage/queues/new">
          {({ history, match }) => (
            <QueueEditDialog
              show={match != null}
              onHide={() => {
                history.push('/manage/queues');
              }}
            />
          )}
        </Route>
        <Route path="/manage/queues/:id/members">
          {({ history, match }) => (
            <QueueMembersDialog
              show={match != null}
              id={match?.params.id}
              setUpdatedRowId={setUpdatedRowId}
              onHide={() => {
                history.push('/manage/queues');
              }}
            />
          )}
        </Route>
        <Route path="/manage/queues/:id/edit">
          {({ history, match }) => (
            <QueueEditDialog
              show={match != null}
              id={match?.params.id}
              setUpdatedRowId={setUpdatedRowId}
              onHide={() => {
                setShowMembersTab(false);
                history.push('/manage/queues');
              }}
              showMembersTab={showMembersTab}
            />
          )}
        </Route>
        <Route path="/manage/queues/deleteQueues">
          {({ history, match }) => (
            <QueuesDeleteDialog
              selected={selected}
              setSelected={setSelected}
              show={match != null}
              onHide={() => {
                history.push('/manage/queues');
              }}
            />
          )}
        </Route>
      </Switch>
      <DataTable
        enableSelectAll={true}
        rows={rowsCached?.current ?? []}
        columns={columns}
        loading={businessQueuesLoadable.state === 'loading'}
        setSelected={setSelected}
        ToolbarComponent={ToolbarComponent}
        updatedRowId={updatedRowId}
        sortModel={sortModel}
        setGridSortModel={setGridSortModel}
        selected={selected}
      />
    </>
  );
}
